import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import api from '../services/api';
import { useWorkspaceStore } from '../stores/workspace.store';
import logoImg from '../assets/logo.png';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
  type Theme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  GridView,
  Campaign,
  ViewList,
  AdsClick,
  Image,
  AutoAwesome,
  Insights,
  Groups,
  Gavel,
  AccountBox,
  Hub,
  BarChart,
  Receipt,
  People,
  Settings,
  Logout,
  Facebook,
  Sensors,
  AccountTree,
  WebAsset,
  CallMade,
  Label,
  History,
  NotificationsActive,
  Savings,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/auth.store';
import { useUiStore } from '../stores/ui.store';
import AutoModals from '../components/AutoModals';
import BirthdayManager from '../components/birthday/BirthdayManager';
import { WorkspaceSelector } from '../components/WorkspaceSelector';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';

const DRAWER_WIDTH = 260;

// Lightness flipped, hue kept: in dark mode the wordmark lifts, the crown stays cyan.
const logoFilter = (t: Theme) => (t.palette.mode === 'dark' ? 'invert(1) hue-rotate(180deg)' : 'none');

type Role = 'admin' | 'media_buyer' | 'viewer' | 'partner';

interface NavItem {
  text: string;
  icon: JSX.Element;
  path: string;
  /** When set, the item only shows for these roles. */
  roles?: Role[];
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { text: 'Dashboards', icon: <GridView />, path: '/dashboards' },
      { text: 'Ads Manager', icon: <Insights />, path: '/ads-manager' },
      { text: 'Profit Share', icon: <Savings />, path: '/profit-share', roles: ['admin'] },
    ],
  },
  {
    label: 'Tracking',
    items: [
      { text: 'Tracking', icon: <BarChart />, path: '/tracking' },
      { text: 'Conversions', icon: <Receipt />, path: '/conversions' },
      { text: 'Click Log', icon: <AdsClick />, path: '/clicks' },
    ],
  },
  {
    label: 'Setup',
    items: [
      { text: 'Traffic Sources', icon: <Sensors />, path: '/traffic-sources' },
      { text: 'Networks', icon: <Hub />, path: '/networks' },
      { text: 'Rule Groups', icon: <AccountTree />, path: '/rule-groups' },
      { text: 'Landing Pages', icon: <WebAsset />, path: '/landing-pages' },
      { text: 'Postbacks', icon: <CallMade />, path: '/postbacks' },
      { text: 'Conversion Types', icon: <Receipt />, path: '/conversion-types' },
      { text: 'Tags', icon: <Label />, path: '/tags' },
      { text: 'Integrations', icon: <Hub />, path: '/ts-integrations', roles: ['admin'] },
      { text: 'Partners', icon: <People />, path: '/partners', roles: ['admin'] },
    ],
  },
  {
    label: 'Ads Management',
    items: [
      { text: 'Campaigns', icon: <Campaign />, path: '/campaigns' },
      { text: 'Ad Sets', icon: <ViewList />, path: '/adsets' },
      { text: 'Ads', icon: <AdsClick />, path: '/ads' },
      { text: 'Ad Accounts', icon: <AccountBox />, path: '/ad-accounts' },
      { text: 'Pages', icon: <Facebook />, path: '/pages' },
      { text: 'Pixels', icon: <Sensors />, path: '/pixels' },
      { text: 'Rules', icon: <Gavel />, path: '/rules' },
      { text: 'Alert Channels', icon: <NotificationsActive />, path: '/alert-channels' },
    ],
  },
  {
    label: 'Creatives',
    items: [
      { text: 'Library', icon: <Image />, path: '/creatives', roles: ['admin'] },
      { text: 'Studio', icon: <AutoAwesome />, path: '/studio', roles: ['admin'] },
    ],
  },
  {
    label: 'Settings',
    items: [
      { text: 'Users', icon: <People />, path: '/users', roles: ['admin'] },
      { text: 'Team Activity', icon: <Groups />, path: '/team-activity', roles: ['admin'] },
      { text: 'MCP Connections', icon: <Hub />, path: '/mcp-connections', roles: ['admin'] },
      { text: 'Audit Log', icon: <History />, path: '/audit', roles: ['admin'] },
    ],
  },
];

export default function DashboardLayout() {
  useActivityHeartbeat();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useUiStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // The selected workspace rides in request params, not the query keys, so switching it must refetch.
  const queryClient = useQueryClient();
  const wsKey = useWorkspaceStore((s) => s.selectedIds.join(','));
  const firstWsRun = useRef(true);
  useEffect(() => {
    if (firstWsRun.current) { firstWsRun.current = false; return; }
    queryClient.invalidateQueries();
  }, [wsKey, queryClient]);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    logout();
    navigate('/login');
  };

  const sidebar = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* The mobile header stays above the drawer (its hamburger closes it), so clear it. */}
      {isMobile && <Toolbar variant="dense" />}
      {/* Logo at the top of the rail (mobile keeps its own in the header). */}
      {!isMobile && (
        <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', justifyContent: 'center' }}>
          <Box component="img" src={logoImg} alt="Neptune" sx={{ height: 46, filter: logoFilter }} />
        </Box>
      )}
      <Box sx={{ px: 2, pb: 1 }}>
        <WorkspaceSelector />
      </Box>
      <Divider />

      <Box sx={{ overflow: 'auto', flexGrow: 1, pt: 1 }}>
        {navSections.map((section, sIdx) => {
          const visibleItems = section.items.filter(
            (item) => !item.roles || (user?.role != null && item.roles.includes(user.role)),
          );
          if (visibleItems.length === 0) return null;
          return (
            <Box key={section.label}>
              {sIdx > 0 && <Divider sx={{ my: 0.5 }} />}
              <Typography
                variant="overline"
                sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.65rem', letterSpacing: 1 }}
              >
                {section.label}
              </Typography>
              <List dense disablePadding>
                {visibleItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      component={RouterLink}
                      to={item.path}
                      selected={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
                      onClick={() => { if (isMobile) setSidebarOpen(false); }}
                      sx={{
                        mx: 1,
                        borderRadius: 1,
                        py: 0.5,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '& .MuiListItemIcon-root': { color: 'inherit' },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* User identity, with Settings + Logout as icon buttons on the right. */}
      <Divider />
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          {user?.name?.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{user?.name || user?.email}</Typography>
          {user?.name && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {user?.email}
            </Typography>
          )}
        </Box>
        <Tooltip title="Settings">
          <IconButton
            component={RouterLink}
            to="/settings"
            size="small"
            color={location.pathname === '/settings' ? 'primary' : 'default'}
            onClick={() => { if (isMobile) setSidebarOpen(false); }}
          >
            <Settings fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Logout">
          <IconButton size="small" onClick={handleLogout}>
            <Logout fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Mobile: a minimal header — hamburger + centered logo. Desktop has no top bar. */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{ zIndex: (t) => t.zIndex.drawer + 1, bgcolor: 'background.paper', color: 'text.primary' }}
        >
          <Toolbar variant="dense">
            <IconButton edge="start" color="inherit" onClick={toggleSidebar}>
              <MenuIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }} />
            <Box component="img" src={logoImg} alt="Neptune" sx={{ height: 36, filter: logoFilter }} />
            <Box sx={{ flexGrow: 1 }} />
            {/* Balances the hamburger so the logo stays centered. */}
            <Box sx={{ width: 40 }} />
          </Toolbar>
        </AppBar>
      )}

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? sidebarOpen : true}
        onClose={() => setSidebarOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? 0 : DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {sidebar}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          minWidth: 0,
          width: isMobile ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)`,
        }}
      >
        {isMobile && <Toolbar variant="dense" />}
        <BirthdayManager />
        <Outlet />
        <AutoModals />
      </Box>
    </Box>
  );
}
